import React, { useRef, useEffect, forwardRef } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

const renderMarkdownCode = (isFollowup = false) => ({
    code({ className, children, inline, ...props }) {
        const match = /language-(\w+)/.exec(className || "");
        if (!inline && match) {
            return (
                <SyntaxHighlighter
                    style={oneLight}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{
                        backgroundColor: isFollowup ? "#eeeeee" : "#fafafa",
                        borderRadius: "6px",
                        padding: "10px",
                        fontSize: "14px",
                        overflowX: "auto"
                    }}
                    {...props}
                >
                    {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
            );
        } else {
            return (
                <code
                    className={className}
                    style={{
                        backgroundColor: isFollowup ? "#e6e6e6" : "#f5f5f5",
                        padding: "2px 4px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                    }}
                    {...props}
                >
                    {children}
                </code>
            );
        }
    }
});

const InterviewQuestion = forwardRef(({ questionBody, followupQuestions, isFollowup, questionNumber }, ref) => {
    const questionContainerRef = useRef(null);

    // Forward the ref to the container div
    useEffect(() => {
        if (ref) {
            if (typeof ref === 'function') {
                ref(questionContainerRef.current);
            } else {
                ref.current = questionContainerRef.current;
            }
        }
    }, [ref]);

    useEffect(() => {
        const container = questionContainerRef.current;
        if (!container) return;

        const isAtBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 50;

        if (isAtBottom) {
            container.scrollTop = container.scrollHeight;
        }
    }, [questionBody, followupQuestions]);

    return (
        <div
            ref={questionContainerRef}
            className="interview_question_container"
            style={{
                border: "1px solid #c9c9c9",
                borderRadius: "10px",
                height: "660px",
                maxHeight: "660px",
                overflowY: "auto",
            }}
        >
            <div className="interview_questionspage_body_heading" style={{ padding: "15px 20px" }} >Description:</div>

            <div style={{ padding: "10px 20px" }}>
                <div
                    className="interview_question_content"
                    style={{
                        backgroundColor: "#FFFFFF",
                        padding: "10px",
                        borderRadius: "5px",
                        fontFamily: "sans-serif",
                    }}
                >
                    <ReactMarkdown
                        children={questionBody}
                        components={renderMarkdownCode()}
                    />
                </div>
            </div>
            {isFollowup ? followupQuestions?.map((item, index) => (
                <div
                    key={index}
                    className="previous_question_box"
                    style={{
                        backgroundColor: "#f9f9f9",
                        padding: "16px",
                        margin: "10px 20px",
                        borderRadius: "10px",
                        borderLeft: "4px solid #c2c2c2",
                        fontSize: "14px",
                        color: "#333",
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: "8px", color: "#444" }}>
                        Follow-up Question {index + 1}
                    </div>
                    <ReactMarkdown children={item.question} components={renderMarkdownCode(true)} />

                    <div style={{ marginTop: "12px", fontWeight: 600, color: "#2e7d32" }}>
                        Your Answer:
                    </div>
                    <div
                        style={{
                            backgroundColor: "#e8f5e9",
                            padding: "10px 14px",
                            borderRadius: "6px",
                            marginTop: "4px",
                            color: "#1b5e20",
                            fontFamily: "sans-serif"
                        }}
                    >
                        <ReactMarkdown children={item.answer} components={renderMarkdownCode(true)} />
                    </div>
                </div>
            )) : null}
        </div>
    );
});

InterviewQuestion.displayName = 'InterviewQuestion';

export default InterviewQuestion;